import {
  categoryWriteResponseSchema,
  localizedProductCategoryResponseSchema,
  localizedProductCategorySchema,
  productCategorySchema,
  publishedCategoryCardResponseSchema,
  reorderProductCategoriesResponseSchema,
  type CategoryWriteResult,
  type ListResult,
  type LocalizedProductCategory,
  type LocalizedText,
  type MerchantModerationState,
  type PaginationParams,
  type ProductCategory,
  type PublishedCategoryCardResponse,
  type ReorderProductCategoriesResult,
} from '../model';
import {
  deleteOne,
  getList,
  getOne,
  patchOne,
  postList,
  postVoid,
  putOne,
  type RequestOptions,
} from './http';

// Product Category Service — the menu taxonomy.
//
// Scope: categories belong to the MERCHANT, not a branch (see
// `model/category.ts`). The merchant comes from the JWT claim, so the listing
// uses the merchant-scoped `by-merchant` endpoint — it returns the full
// catalog INCLUDING categories with no products yet, which the client-facing
// listing would not surface. `restaurant_id` narrows nothing in practice
// (two branches of one merchant return an identical set), so callers should
// not rely on it to scope a branch's menu.

/**
 * Filters for `GET /merchant/product-categories/published` — published categories.
 */
export type ListPublishedCategoriesParams = PaginationParams & {
  restaurant_id?: string;
};

/**
 * Filters for `GET /merchant/product-categories`.
 *
 * `restaurant_id` is genuinely optional here — omitted, it returns the
 * merchant's full catalog. (On the client-facing `GET /client/product-categories` it
 * is required: omitting it is a 400 `invalid input` naming the field.)
 */
export type ListProductCategoriesParams = PaginationParams & {
  restaurant_id?: string;
  name?: string;
  /**
   * Moderation state of the category. Sent as `state=MERCHANT_MODERATION_STATE_*`
   * per the merchant category list contract.
   */
  state?: MerchantModerationState;
};

/**
 * `PATCH /merchant/product-categories/{id}` body.
 *
 * `name` is a `v1LocalizedText`, not a plain string — the merchant write RPCs
 * speak translations (omit it to leave the name untouched).
 *
 * NO `icon` member: the v1.00.158 body dropped it, and the gateway REJECTS
 * unknown fields (400) rather than ignoring them.
 */
export interface UpdateProductCategoryInput {
  name?: LocalizedText;
  is_active?: boolean;
}

export interface CategoryPosition {
  category_id: string;
  sort_order: number;
}

export const productCategoryApi = {
  /** Set up to 50 category positions, numbered from 1. */
  reorder(positions: CategoryPosition[]): Promise<ReorderProductCategoriesResult> {
    if (
      positions.length === 0 ||
      positions.length > 50 ||
      positions.some(({ sort_order }) => !Number.isInteger(sort_order) || sort_order < 1)
    ) {
      throw new RangeError(
        'Expected 1–50 category positions with sort_order starting at 1',
      );
    }
    return putOne(
      'merchant/product-categories/order',
      { positions },
      reorderProductCategoriesResponseSchema,
    );
  },
  /**
   * List the published menu categories visible to clients.
   * `GET /merchant/product-categories/published`. `restaurant_id` is optional.
   */
  listPublished(
    params?: ListPublishedCategoriesParams,
    options?: RequestOptions,
  ): Promise<ListResult<ProductCategory>> {
    return getList(
      'merchant/product-categories/published',
      productCategorySchema,
      params,
      options,
    );
  },

  /**
   * Get a single published category card — `GET /merchant/product-categories/{id}/published`.
   * Answers with `{ client_visible: true, category }` or `{ client_visible: false, category: null }`.
   */
  getPublished(
    id: string,
    options?: RequestOptions,
  ): Promise<PublishedCategoryCardResponse> {
    return getOne(
      `merchant/product-categories/${id}/published`,
      publishedCategoryCardResponseSchema,
      undefined,
      options,
    );
  },

  /**
   * List the merchant's menu categories, including ones with no products yet.
   *
   * Answers with the locale-resolved `v1ProductCategory` (a plain string
   * `name`), not the `Localized*` shape the write RPCs return — this is a read
   * RPC. See `model/category.ts`.
   */
  list(
    params?: ListProductCategoriesParams,
    options?: RequestOptions,
  ): Promise<ListResult<ProductCategory>> {
    return getList('merchant/product-categories', productCategorySchema, params, options);
  },

  /**
   * Get a single category by id — `GET /merchant/product-categories/{id}`.
   *
   * Answers with `v1LocalizedProductCategory`: EVERY translation in one call
   * (new since v1.00.155's spec sync). This retires the old workaround of
   * reading the same category once per language with `x-locale` and
   * reassembling — and the client-facing `GET /client/product-categories/{id}` it
   * replaced is no longer a merchant route at all.
   */
  async get(id: string, options?: RequestOptions): Promise<LocalizedProductCategory> {
    const { category } = await getOne(
      `merchant/product-categories/${id}`,
      localizedProductCategoryResponseSchema,
      undefined,
      options,
    );
    return category;
  },

  /**
   * Create a category. The endpoint takes `names` as a LIST of translation sets
   * (`v1LocalizedText[]`) and answers with a list envelope, so one call carries
   * one category's three languages — not several categories.
   *
   * `merchant_id` is documented on the body but deliberately not sent, and
   * `restaurant_id` is not sent either — the endpoint no longer accepts it.
   * The auth service issues the merchant as a JWT claim and the backend derives it from
   * the bearer token (see the app's CLAUDE.md §8).
   */
  create(names: LocalizedText[]): Promise<ListResult<LocalizedProductCategory>> {
    return postList(
      'merchant/product-categories',
      { names },
      localizedProductCategorySchema,
    );
  },

  /**
   * Rename / toggle a category.
   *
   * Answers with EITHER the updated category in the LOCALIZED shape (`name`
   * as all three translations) or, when the merchant's writes are gated
   * behind moderation, only a `pending_change` receipt. Both are successes,
   * so the result is returned as-is rather than unwrapped to a category the
   * caller might not have received.
   */
  update(id: string, input: UpdateProductCategoryInput): Promise<CategoryWriteResult> {
    return patchOne(
      `merchant/product-categories/${id}`,
      input,
      categoryWriteResponseSchema,
    );
  },

  /** Delete a category. */
  remove(id: string): Promise<void> {
    return deleteOne(`merchant/product-categories/${id}`);
  },

  /** Drop merchant overrides so the named fields follow the menu source again. */
  resetOverrides(id: string, fields: string[]): Promise<void> {
    return postVoid(`product-categories/${id}/reset-overrides`, { fields });
  },
};
